import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  Plus, 
  Trash2, 
  Play, 
  AlertTriangle,
  Users,
  TrendingUp,
  Shield,
  Search,
  ChevronDown,
  ChevronUp,
  Check,
  RefreshCw,
  Clock
} from 'lucide-react';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';
import './Rules.css';

// Utility function to format current time (already local)
const formatCurrentTime = (date) => {
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });
};



const Rules = () => {
  const [selectedRule, setSelectedRule] = useState(null);
  const [showTestPanel, setShowTestPanel] = useState(false);
  const [testMessage, setTestMessage] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [isTesting, setIsTesting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [expandedRules, setExpandedRules] = useState(new Set());
  const [showAddRuleModal, setShowAddRuleModal] = useState(false);
  const [newRule, setNewRule] = useState({
    name: '',
    category: 'MNPI',
    definition: '',
    examples: [''],
    example_phrases: [''],
    contra_rules: [''],
    risk_score: 70,
    is_active: true
  });
  const queryClient = useQueryClient();

  // Fetch rules from API
  const { data: rulesResponse, isLoading: rulesLoading, error: rulesError, refetch: refetchRules } = useQuery(
    'rules',
    apiService.getRules,
    {
      refetchInterval: 30000, // Refresh every 30 seconds
      retry: 3,
    }
  );

  const rules = rulesResponse?.rules || [];

  // Create rule mutation
  const createRuleMutation = useMutation(
    async (ruleData) => {
      return apiService.createRule(ruleData);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('rules');
        toast.success('Rule created successfully');
        setShowAddRuleModal(false);
        setNewRule({
          name: '',
          category: 'MNPI',
          definition: '',
          examples: [''],
          example_phrases: [''],
          contra_rules: [''],
          risk_score: 70,
          is_active: true
        });
      },
      onError: (error) => {
        toast.error('Failed to create rule');
        console.error('Create rule error:', error);
      }
    }
  );

  // Delete rule mutation
  const deleteRuleMutation = useMutation(
    async (ruleId) => {
      return apiService.deleteRule(ruleId);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('rules');
        toast.success('Rule deleted successfully');
      },
      onError: (error) => {
        toast.error('Failed to delete rule');
        console.error('Delete rule error:', error);
      }
    }
  );

  // Helper function to get icon and color for rule category
  const getRuleIcon = (category) => {
    switch (category) {
      case 'MNPI': return AlertTriangle;
      case 'FRONT_RUNNING': return TrendingUp;
      case 'COLLUSION': return Users;
      case 'MISCONDUCT': return Shield;
      default: return AlertTriangle;
    }
  };

  const getRuleColor = (category) => {
    switch (category) {
          case 'MNPI': return '#a35252';
    case 'FRONT_RUNNING': return '#a36b52';
    case 'COLLUSION': return '#a35252';
    case 'MISCONDUCT': return '#a35252';
    default: return '#a35252';
    }
  };

  const filteredRules = rules.filter(rule => {
    const matchesSearch = rule.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         rule.definition.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !filterCategory || rule.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  // Sort the filtered rules
  const sortedRules = [...filteredRules].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortBy) {
      case 'name':
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
        break;
      case 'risk':
        aValue = a.risk_score;
        bValue = b.risk_score;
        break;
      case 'category':
        aValue = a.category.toLowerCase();
        bValue = b.category.toLowerCase();
        break;
      default:
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
    }
    
    if (sortOrder === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  const handleSortChange = (newSortBy) => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('asc');
    }
  };

  const toggleRuleExpansion = (ruleId) => {
    setExpandedRules(prev => {
      const newSet = new Set(prev);
      if (newSet.has(ruleId)) {
        newSet.delete(ruleId);
      } else {
        newSet.add(ruleId);
      }
      return newSet;
    });
  };

  const handleAddArrayItem = (field) => {
    setNewRule(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const handleRemoveArrayItem = (field, index) => {
    setNewRule(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleArrayItemChange = (field, index, value) => {
    setNewRule(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const handleSubmitNewRule = (e) => {
    e.preventDefault();
    
    // Filter out empty items from arrays
    const ruleData = {
      ...newRule,
      examples: newRule.examples.filter(item => item.trim() !== ''),
      example_phrases: newRule.example_phrases.filter(item => item.trim() !== ''),
      contra_rules: newRule.contra_rules.filter(item => item.trim() !== '')
    };
    
    createRuleMutation.mutate(ruleData);
  };

  const handleTestRule = async () => {
    if (!testMessage.trim()) return;
    
    setIsTesting(true);
    setTestResult(null);
    
    // Simulate API call
    setTimeout(() => {
      const mockResult = {
        triggered: Math.random() > 0.5,
        riskScore: Math.floor(Math.random() * 100),
        matchedRules: Math.random() > 0.7 ? [selectedRule.name] : [],
        reasoning: 'This message contains potential compliance violations based on the current rule set...'
      };
      
      setTestResult(mockResult);
      setIsTesting(false);
    }, 1500);
  };

  const handleDeleteRule = (ruleId) => {
    if (window.confirm('Are you sure you want to delete this rule? This action cannot be undone.')) {
      deleteRuleMutation.mutate(ruleId);
    }
  };

  const RuleCard = ({ rule, isExpanded, onToggle }) => {
    const Icon = getRuleIcon(rule.category);
    const color = getRuleColor(rule.category);
    
    return (
      <div className={`rule-card ${isExpanded ? 'expanded' : ''}`}>
        <div className="rule-header" onClick={() => onToggle(rule.id)}>
          <div className="rule-info">
            <Icon className="rule-icon" style={{ color: color }} />
            <div className="rule-details">
              <h3 className="rule-name">{rule.name}</h3>
              <span className="rule-category">{rule.category}</span>
            </div>
          </div>
          <div className="rule-actions">
            <span className="risk-score" style={{ backgroundColor: color }}>
              {rule.risk_score}
            </span>
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
        </div>
        
        {isExpanded && (
          <div className="rule-content">
            <div className="rule-section">
              <h4>Definition</h4>
              <p>{rule.definition}</p>
            </div>
            
            <div className="rule-section">
              <h4>Examples</h4>
              <ul>
                {rule.examples.map((example, index) => (
                  <li key={index}>{example}</li>
                ))}
              </ul>
            </div>
            
            <div className="rule-section">
              <h4>Example Phrases</h4>
              <div className="example-phrases">
                {rule.example_phrases.map((phrase, index) => (
                  <code key={index} className="phrase-example">{phrase}</code>
                ))}
              </div>
            </div>
            
            <div className="rule-section">
              <h4>Contra Rules</h4>
              <ul>
                {rule.contra_rules.map((contra, index) => (
                  <li key={index}>{contra}</li>
                ))}
              </ul>
            </div>
            
            <div className="rule-actions-bar">
              <button 
                className="btn btn-danger btn-sm"
                onClick={() => handleDeleteRule(rule.id)}
                disabled={deleteRuleMutation.isLoading}
              >
                <Trash2 className="btn-icon" />
                {deleteRuleMutation.isLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="rules-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <h1>Compliance Rules</h1>
          <div className="header-actions">
            <div className="last-updated">
              <Clock className="clock-icon" />
              <span>Updated {formatCurrentTime(new Date())}</span>
            </div>
            <button 
              className="refresh-btn"
              onClick={() => refetchRules()}
              disabled={rulesLoading}
              title="Refresh rules"
            >
              <RefreshCw className={`refresh-icon ${rulesLoading ? 'spinning' : ''}`} size={16} />
              Refresh
            </button>
            <button 
              className="refresh-btn"
              onClick={() => setShowTestPanel(!showTestPanel)}
            >
              <Play className="refresh-icon" size={16} />
              Test Rules
            </button>
            <button 
              className="refresh-btn"
              onClick={() => setShowAddRuleModal(true)}
              style={{ backgroundColor: 'var(--success)' }}
            >
              <Plus className="refresh-icon" size={16} />
              Add Rule
            </button>
          </div>
        </div>
      </div>

      {/* Test Panel */}
      {showTestPanel && (
        <div className="test-panel">
          <div className="card">
            <div className="card-header">
              <h3>Rule Testing Interface</h3>
            </div>
            <div className="card-body">
              <div className="test-interface">
                <div className="test-input">
                  <h4>Test Message</h4>
                  <textarea
                    className="form-control"
                    value={testMessage}
                    onChange={(e) => setTestMessage(e.target.value)}
                    placeholder="Enter a message to test against all rules..."
                    rows={4}
                  />
                  <div className="test-controls">
                    <button 
                      className="btn btn-primary"
                      onClick={handleTestRule}
                      disabled={!testMessage.trim() || isTesting}
                    >
                      {isTesting ? 'Testing...' : 'Test Message'}
                    </button>
                    <button 
                      className="btn btn-secondary"
                      onClick={() => setTestMessage('')}
                    >
                      Clear
                    </button>
                  </div>
                </div>
                
                <div className="test-result">
                  <h4>Test Result</h4>
                  {isTesting ? (
                    <div className="loading-result">
                      <div className="loading-spinner"></div>
                      <p>Analyzing message...</p>
                    </div>
                  ) : testResult ? (
                    <div className="result-content">
                      <div className="result-summary">
                        <div className={`result-indicator ${testResult.triggered ? 'triggered' : 'clean'}`}>
                          {testResult.triggered ? (
                            <>
                              <AlertTriangle className="result-icon" />
                              <span>Violations Detected</span>
                            </>
                          ) : (
                            <>
                              <Check className="result-icon" />
                              <span>No Violations</span>
                            </>
                          )}
                        </div>
                        <div className="risk-score-display">
                          <span className="score-label">Risk Score:</span>
                          <span className="score-value" style={{ 
                                    backgroundColor: testResult.riskScore > 70 ? '#a35252' :
                        testResult.riskScore > 50 ? '#a36b52' :
                        testResult.riskScore > 20 ? '#a3a352' : '#00a352'
                          }}>
                            {testResult.riskScore}
                          </span>
                        </div>
                      </div>
                      
                      {testResult.matchedRules.length > 0 && (
                        <div className="matched-rules">
                          <h5>Matched Rules:</h5>
                          <ul>
                            {testResult.matchedRules.map((rule, index) => (
                              <li key={index}>{rule}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      <div className="reasoning">
                        <h5>Analysis:</h5>
                        <p>{testResult.reasoning}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="no-result">
                      <p>Enter a message and click "Test Message" to see results</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters-section">
        <div className="card">
          <div className="card-body">
            <div className="filters-grid">
              <div className="form-group">
                <label className="form-label">Search Rules</label>
                <div className="search-input">
                  <Search className="search-icon" />
                  <input
                    type="text"
                    className="form-control"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search rules by name or definition..."
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Category</label>
                <select
                  className="form-control"
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                >
                  <option value="">All Categories</option>
                  <option value="MNPI">MNPI</option>
                  <option value="FRONT_RUNNING">Front Running</option>
                  <option value="COLLUSION">Collusion</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Sort By</label>
                <select 
                  className="form-control"
                  value={sortBy}
                  onChange={(e) => handleSortChange(e.target.value)}
                >
                  <option value="name">Name</option>
                  <option value="risk">Risk Score</option>
                  <option value="category">Category</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Order</label>
                <select 
                  className="form-control"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                >
                  <option value="asc">Ascending</option>
                  <option value="desc">Descending</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Rules List */}
      <div className="rules-section">
        <div className="section-header">
          <h2>Rules ({sortedRules.length})</h2>
          <div className="rules-summary">
            <span>Total Rules: {rules.length}</span>
            <span>Active Rules: {rules.length}</span>
            <span>Sort: {sortBy} ({sortOrder})</span>
          </div>
        </div>

        <div className="rules-list">
          {rulesLoading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading rules...</p>
            </div>
          ) : rulesError ? (
            <div className="error-container">
              <AlertTriangle className="error-icon" />
              <h3>Error loading rules</h3>
              <p>{rulesError.message}</p>
            </div>
          ) : sortedRules.length > 0 ? (
            sortedRules.map((rule) => (
              <RuleCard
                key={rule.id}
                rule={rule}
                isExpanded={expandedRules.has(rule.id)}
                onToggle={toggleRuleExpansion}
              />
            ))
          ) : (
            <div className="no-rules">
              <Shield className="no-rules-icon" />
              <h3>No rules found</h3>
              <p>Try adjusting your search criteria or create a new rule</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="stats-section">
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-header">
              <AlertTriangle className="stat-icon red" />
            </div>
            <div className="stat-content">
              <h3 className="stat-value">{rulesLoading ? '...' : rules.length}</h3>
              <p className="stat-title">Total Rules</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-header">
              <Users className="stat-icon orange" />
            </div>
            <div className="stat-content">
              <h3 className="stat-value">
                {rulesLoading ? '...' : new Set(rules.map(r => r.category)).size}
              </h3>
              <p className="stat-title">Active Categories</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-header">
              <Shield className="stat-icon green" />
            </div>
            <div className="stat-content">
              <h3 className="stat-value">
                {rulesLoading ? '...' : rules.length > 0 ? 
                  (rules.reduce((sum, r) => sum + r.risk_score, 0) / rules.length).toFixed(1) : '0'
                }
              </h3>
              <p className="stat-title">Avg Risk Score</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-header">
              <Check className="stat-icon blue" />
            </div>
            <div className="stat-content">
              <h3 className="stat-value">
                {rulesLoading ? '...' : rules.filter(r => r.is_active).length}
              </h3>
              <p className="stat-title">Active Rules</p>
            </div>
          </div>
        </div>
      </div>

      {/* Add Rule Modal */}
      {showAddRuleModal && (
        <div className="modal-overlay" onClick={() => setShowAddRuleModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New Rule</h3>
              <button 
                className="btn btn-sm"
                onClick={() => setShowAddRuleModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmitNewRule}>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Rule Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={newRule.name}
                      onChange={(e) => setNewRule(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., MNPI (Material Non-Public Information)"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Category *</label>
                    <select
                      className="form-control"
                      value={newRule.category}
                      onChange={(e) => setNewRule(prev => ({ ...prev, category: e.target.value }))}
                      required
                    >
                      <option value="MNPI">MNPI</option>
                      <option value="FRONT_RUNNING">Front Running</option>
                      <option value="COLLUSION">Collusion</option>
                      <option value="MISCONDUCT">Misconduct</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Risk Score *</label>
                    <input
                      type="number"
                      className="form-control"
                      value={newRule.risk_score}
                      onChange={(e) => setNewRule(prev => ({ ...prev, risk_score: parseInt(e.target.value) }))}
                      min="0"
                      max="100"
                      required
                    />
                  </div>
                  
                  <div className="form-group full-width">
                    <label className="form-label">Definition *</label>
                    <textarea
                      className="form-control"
                      value={newRule.definition}
                      onChange={(e) => setNewRule(prev => ({ ...prev, definition: e.target.value }))}
                      placeholder="Describe what this rule covers..."
                      rows={3}
                      required
                    />
                  </div>
                  
                  <div className="form-group full-width">
                    <label className="form-label">Examples</label>
                    {newRule.examples.map((example, index) => (
                      <div key={index} className="array-item">
                        <input
                          type="text"
                          className="form-control"
                          value={example}
                          onChange={(e) => handleArrayItemChange('examples', index, e.target.value)}
                          placeholder={`Example ${index + 1}`}
                        />
                        {newRule.examples.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => handleRemoveArrayItem('examples', index)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleAddArrayItem('examples')}
                    >
                      + Add Example
                    </button>
                  </div>
                  
                  <div className="form-group full-width">
                    <label className="form-label">Example Phrases</label>
                    {newRule.example_phrases.map((phrase, index) => (
                      <div key={index} className="array-item">
                        <input
                          type="text"
                          className="form-control"
                          value={phrase}
                          onChange={(e) => handleArrayItemChange('example_phrases', index, e.target.value)}
                          placeholder={`Example phrase ${index + 1}`}
                        />
                        {newRule.example_phrases.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => handleRemoveArrayItem('example_phrases', index)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleAddArrayItem('example_phrases')}
                    >
                      + Add Example Phrase
                    </button>
                  </div>
                  
                  <div className="form-group full-width">
                    <label className="form-label">Contra Rules</label>
                    {newRule.contra_rules.map((contra, index) => (
                      <div key={index} className="array-item">
                        <input
                          type="text"
                          className="form-control"
                          value={contra}
                          onChange={(e) => handleArrayItemChange('contra_rules', index, e.target.value)}
                          placeholder={`Contra rule ${index + 1}`}
                        />
                        {newRule.contra_rules.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => handleRemoveArrayItem('contra_rules', index)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleAddArrayItem('contra_rules')}
                    >
                      + Add Contra Rule
                    </button>
                  </div>
                </div>
                
                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowAddRuleModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={createRuleMutation.isLoading}
                  >
                    {createRuleMutation.isLoading ? 'Creating...' : 'Create Rule'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Rules; 